import { useEffect, useRef } from 'react';
import { trackEvent } from '@/lib/analytics';

type ScrollMilestone = 25 | 50 | 75 | 90 | 100;

const MILESTONES: ScrollMilestone[] = [25, 50, 75, 90, 100];

/**
 * Hook to track scroll depth on a page
 * Fires events at 25%, 50%, 75%, 90%, and 100% scroll milestones
 * Each milestone is only tracked once per page load
 */
export function useScrollDepthTracking(pageName: string): void {
  const trackedMilestones = useRef<Set<ScrollMilestone>>(new Set());

  useEffect(() => {
    // Reset tracked milestones when page changes
    trackedMilestones.current = new Set();

    const calculateScrollPercentage = (): number => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      
      if (scrollHeight <= 0) return 100; // Page doesn't scroll
      
      return Math.round((scrollTop / scrollHeight) * 100);
    };

    const handleScroll = (): void => {
      const scrollPercentage = calculateScrollPercentage();

      for (const milestone of MILESTONES) {
        if (
          scrollPercentage >= milestone &&
          !trackedMilestones.current.has(milestone)
        ) {
          trackedMilestones.current.add(milestone);
          
          trackEvent('scroll_depth', {
            page_path: window.location.pathname,
            page_name: pageName,
            depth_percentage: milestone,
            depth_threshold: `${milestone}%`,
          });
        }
      }
    };

    // Check initial scroll position (in case user refreshes mid-page)
    handleScroll();

    // Use passive listener for better scroll performance
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [pageName]);
}
